import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';
import { gstTotals } from '../lib/storeConfig';
import { GiftCardCartSection } from '../components/GiftCardCartSection';
import { GiftCardCartCheckout } from '../components/GiftCardCartCheckout';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const { count: giftCardCount } = useGiftCardCart();
  const { shippingFor, freeThreshold, defaultGstRate } = useSettings();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setApplyingCoupon(true);
    try {
      const response = await fetch(`${API}/coupons/validate?code=${couponCode}&subtotal=${cart.total}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Invalid coupon');
      }

      const data = await response.json();
      setDiscount(data.discount);
      setAppliedCoupon(couponCode.toUpperCase());
      toast.success(`Coupon applied! You save ${formatPrice(data.discount)}`);
    } catch (error) {
      toast.error(error.message);
      setDiscount(0);
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setDiscount(0);
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const subtotal = cart.total;
  // Rates come from admin Settings, not from a hardcoded constant, so a saved change
  // shows up on the storefront immediately.
  const shipping = shippingFor(subtotal);

  // GST is already inside the listed prices — this only shows how much of the
  // subtotal is tax. A product configured at 0% contributes ₹0: the old
  // `item.product?.igst || defaultGstRate` read 0 as "unset" and charged 18%.
  const { tax: totalTax } = gstTotals(
    cart.items.map((item) => ({ amount: item.item_total, rates: item.product })),
    { defaultRate: defaultGstRate }
  );

  const total = subtotal - discount + shipping;

  // Remove the "!user" check that was forcing login

  // A basket holding only gift cards is not an empty basket. Gift cards live in
  // their own store (see GiftCardCartContext), so `cart.items` alone would have
  // told a customer with six vouchers waiting that they had added nothing.
  if (cart.items.length === 0 && giftCardCount > 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-page">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
          <div className="mb-8 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="rounded-full p-2 transition-colors hover:bg-surface"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-heading text-3xl text-secondary md:text-4xl">Shopping Cart</h1>
              <p className="font-body text-muted-foreground">
                {giftCardCount} gift {giftCardCount === 1 ? 'card' : 'cards'}
              </p>
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <GiftCardCartSection />
            </div>
            <div className="lg:col-span-1">
              <GiftCardCartCheckout />
              <Link
                to="/"
                className="mt-4 block text-center font-body text-sm text-muted-foreground hover:text-primary"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-page-empty">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="text-center max-w-md mx-auto">
            <ShoppingBag className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="font-heading text-3xl text-secondary mb-4">Your Cart is Empty</h1>
            <p className="text-muted-foreground font-body mb-8">
              Looks like you haven't added anything to your cart yet.
            </p>
            <Button
              asChild
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
            >
              <Link to="/">Start Shopping</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-surface rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl text-secondary">Shopping Cart</h1>
            <p className="text-muted-foreground font-body">
              {cart.items.length} {cart.items.length === 1 ? 'product' : 'products'}
              {giftCardCount > 0 && ` · ${giftCardCount} gift ${giftCardCount === 1 ? 'card' : 'cards'}`}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            <GiftCardCartSection />
            {giftCardCount > 0 && (
              <h2 className="pt-2 font-heading text-xl text-secondary">Products</h2>
            )}
            {cart.items.map((item) => (
              <div
                key={item.product_id}
                className="bg-surface rounded-3xl p-4 md:p-6 flex gap-4"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link
                  to={`/product/${item.product_id}`}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden"
                >
                  <img
                    src={getImageUrl(item.product?.images?.[0])}
                    alt={item.product?.name}
                    onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                    className="max-w-full max-h-full object-contain p-2"
                    loading="lazy"
                    decoding="async"
                  />
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-body font-medium text-secondary hover:text-primary transition-colors line-clamp-2">
                      {item.product?.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    {item.product?.brand}
                  </p>
                  <p className="font-body font-semibold text-secondary mt-2">
                    {formatPrice(item.product?.price)}
                  </p>

                  {/* Quantity & Actions */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-gray-200 rounded-full bg-white">
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                        className="p-2 hover:bg-surface transition-colors rounded-l-full"
                        disabled={loading}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-body text-sm">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                        className="p-2 hover:bg-surface transition-colors rounded-r-full"
                        disabled={loading || item.quantity >= item.product?.stock}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemove(item.product_id)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-full transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Item Total - Desktop */}
                <div className="hidden md:block text-right">
                  <p className="text-sm text-muted-foreground font-body">Total</p>
                  <p className="font-body font-semibold text-secondary text-lg">
                    {formatPrice(item.item_total)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1 space-y-6">
            {/* Gift cards settle on their own — separate money, separate button. */}
            <GiftCardCartCheckout />

            <div className="bg-surface rounded-3xl p-6" data-testid="order-summary">
              <h2 className="font-heading text-xl text-secondary mb-6">Order Summary</h2>

              {/* Coupon */}
              <div className="mb-6">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-success/10 p-3 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-success" />
                      <span className="font-body text-sm text-success font-medium">{appliedCoupon}</span>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-sm text-destructive hover:underline font-body"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="rounded-xl"
                      data-testid="coupon-input"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon || !couponCode.trim()}
                      variant="outline"
                      className="rounded-xl px-4"
                      data-testid="apply-coupon-btn"
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="space-y-3 text-sm font-body">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-secondary font-medium">{formatPrice(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-success">
                    <span>Discount</span>
                    <span>-{formatPrice(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-secondary font-medium">
                    {shipping === 0 ? 'FREE' : formatPrice(shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground italic border-t border-dashed border-gray-100 pt-2">
                  <span>Total Tax (Incl. GST)</span>
                  <span>{formatPrice(totalTax)}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Free shipping on orders above {formatPrice(freeThreshold)}
                  </p>
                )}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-secondary">Total</span>
                    <span className="font-semibold text-secondary text-lg">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <Button
                onClick={() => {
                  // Persist alongside the router state: router state does not survive a
                  // page refresh, and losing the discount silently is worse than any
                  // other checkout bug because the customer is still charged.
                  if (appliedCoupon && discount > 0) {
                    sessionStorage.setItem(
                      'applied_coupon',
                      JSON.stringify({ coupon: appliedCoupon, discount })
                    );
                  } else {
                    sessionStorage.removeItem('applied_coupon');
                  }

                  if (!user) {
                    navigate('/login?redirect=/checkout');
                  } else {
                    navigate('/checkout', { state: { discount, coupon: appliedCoupon } });
                  }
                }}
                className="w-full mt-6 bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
              </Button>

              <Link
                to="/"
                className="block text-center mt-4 text-sm text-muted-foreground hover:text-primary font-body"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
